import * as Sentry from "@sentry/react";
import constate from "constate";
import { useState, useRef, useCallback, useEffect } from "react";
import { t as trackSegmentEvent, r as resetSegmentUser, i as identifySegmentUser, l as logSegmentPageView, a as ApiClient, A as ApiError } from "./api-BH0Z-83g.js";
import { c as clearBrowserStorage, B as BrowserStorageType } from "./utils-DueGrJ09.js";
import { u as useLaunchDarklyUpdater } from "./useLaunchDarklyUpdater-qS2r1zS0.js";
import { a as appendQueryParams } from "./utils-CztPRCN_.js";
import { useAuthSession, useAuthenticatedUser, SESSIONS_ENDPOINT } from "../AuthContextApis.js";
var UserRole = /* @__PURE__ */ ((UserRole2) => {
  UserRole2["USER_WITH_ACCOUNT"] = "user_with_account";
  UserRole2["NO_TEST"] = "no_test";
  UserRole2["GENETICS"] = "genetics";
  UserRole2["COVID_NON_DEPENDENT_PROXY"] = "covid_proxy";
  UserRole2["COVID_FULL_USER_OR_DEPENDENT"] = "covid_full_user_or_dependent";
  UserRole2["CARE_FULL_USER_OR_DEPENDENT"] = "care_full_user_or_dependent";
  UserRole2["ESSENTIAL_CARE_USER"] = "essential_care_user";
  UserRole2["STI_TEST_USER"] = "sti_test_user";
  UserRole2["COLLECTION_SITE_ADMIN"] = "collection_site_admin";
  UserRole2["COVID_SITE_MANAGER"] = "covid_site_manager";
  UserRole2["PROVIDER"] = "provider";
  UserRole2["SUPPORT_STAFF"] = "support_staff";
  UserRole2["COLOR_EMPLOYEE"] = "color_employee";
  UserRole2["DASHBOARD_VIEWER"] = "dashboard_viewer";
  UserRole2["ACCESS_MANAGER"] = "access_manager";
  UserRole2["SUPPORT_ADMIN"] = "support_admin";
  UserRole2["ELIGIBILITY_DATA_MANAGER"] = "eligibility_data_manager";
  UserRole2["SAMPLE_REVIEWER"] = "sample_reviewer";
  UserRole2["ORDERING_PHYSICIAN"] = "ordering_physician";
  UserRole2["GENETIC_COUNSELOR"] = "genetic_counselor";
  UserRole2["EXTERNAL_CLASSIFICATION_REVIEWER"] = "external_classification_reviewer";
  UserRole2["INTERNAL_CLASSIFICATION_REVIEWER"] = "internal_classification_reviewer";
  UserRole2["VARIANT_REVIEWER"] = "variant_reviewer";
  UserRole2["VARIANT_REVIEWER_MANAGER"] = "variant_reviewer_manager";
  UserRole2["GENETICS_BOARD_MEMBER"] = "genetics_board_member";
  UserRole2["PATHOLOGIST"] = "pathologist";
  UserRole2["FULFILLMENT_STAFF"] = "fulfillment_staff";
  UserRole2["CONTACT_MANAGER"] = "contact_manager";
  UserRole2["VACCINATION_PLATFORM_SITE_MANAGER"] = "vaccination_platform_site_manager";
  UserRole2["VACCINATION_PLATFORM_STAFF_MANAGER"] = "vaccination_platform_staff_manager";
  UserRole2["VACCINATION_PLATFORM_SUPER_ADMIN"] = "vaccination_platform_super_admin";
  UserRole2["VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER"] = "vaccination_platform_site_inventory_manager";
  UserRole2["VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER"] = "vaccination_platform_bulk_inventory_manager";
  UserRole2["VACCINATION_PLATFORM_SITE_STAFF"] = "vaccination_platform_site_staff";
  UserRole2["VACCINATION_PLATFORM_SCRIBE"] = "vaccination_platform_scribe";
  UserRole2["VACCINATION_PLATFORM_VACCINATING_CLINICIAN"] = "vaccination_platform_vaccinating_clinician";
  UserRole2["LAB_STAFF"] = "lab_staff";
  return UserRole2;
})(UserRole || {});
const ALL_USER_ROLES = [
  "no_test",
  "genetics",
  "covid_proxy",
  "care_full_user_or_dependent",
  "covid_full_user_or_dependent",
  "essential_care_user",
  "sti_test_user",
  "covid_site_manager",
  "provider",
  "support_staff",
  "color_employee",
  "dashboard_viewer",
  "access_manager",
  "eligibility_data_manager",
  "collection_site_admin",
  "fulfillment_staff",
  "contact_manager",
  "vaccination_platform_site_manager",
  "vaccination_platform_staff_manager",
  "vaccination_platform_super_admin"
  /* VACCINATION_PLATFORM_SUPER_ADMIN */
];
const GENETICS_USER_ROLES = [
  "genetics",
  "no_test"
  /* NO_TEST */
];
var CollectionSiteRole = /* @__PURE__ */ ((CollectionSiteRole2) => {
  CollectionSiteRole2["ONSITE_STAFF"] = "sample_collection_admin";
  CollectionSiteRole2["BATCH_SHIPMENT_ADMIN"] = "batch_shipment_admin";
  CollectionSiteRole2["SITE_SCHEDULER"] = "site_scheduler";
  CollectionSiteRole2["SITE_MANAGER"] = "site_manager";
  CollectionSiteRole2["STAFF_MANAGER"] = "staff_manager";
  CollectionSiteRole2["VACCINE_INVENTORY_MANAGER"] = "vaccine_inventory_manager";
  CollectionSiteRole2["VACCINE_CLINICIAN"] = "vaccine_clinician";
  CollectionSiteRole2["SCRIBE"] = "scribe";
  return CollectionSiteRole2;
})(CollectionSiteRole || {});
var CollectionSiteRoleCategory = /* @__PURE__ */ ((CollectionSiteRoleCategory2) => {
  CollectionSiteRoleCategory2["GENERAL"] = "general";
  CollectionSiteRoleCategory2["APPOINTMENT_MANAGEMENT"] = "appointment_management";
  CollectionSiteRoleCategory2["INVENTORY_MANAGEMENT"] = "inventory_management";
  CollectionSiteRoleCategory2["ONSITE_PATIENT_MANAGEMENT"] = "onsite_patient_management";
  CollectionSiteRoleCategory2["BATCH_SHIPMENT"] = "batch_shipment";
  return CollectionSiteRoleCategory2;
})(CollectionSiteRoleCategory || {});
var UserPermission = /* @__PURE__ */ ((UserPermission2) => {
  UserPermission2["PUT_SAMPLES_ON_HOLD"] = "samples.put_sample_on_hold";
  UserPermission2["VIEW_USERS"] = "users.view_users";
  UserPermission2["VIEW_PHI"] = "users.view_phi";
  UserPermission2["VIEW_APPOINTMENTS"] = "counseling.view_appointments";
  UserPermission2["VIEW_HEALTH_HISTORY"] = "health_profiles.view_healthhistory";
  UserPermission2["VIEW_COVID_REPORTS_QUEUE"] = "results.can_view_covid_reports_queue";
  UserPermission2["ACCESS_PATIENT_DATA"] = "colorhub.access_patient_data";
  UserPermission2["ACCESS_SKIN_CLINIC"] = "colorhub.access_skin_clinic";
  UserPermission2["EDIT_SKIN_CLINIC"] = "colorhub.edit_skin_clinic";
  UserPermission2["REVIEW_SKIN_CLINIC"] = "colorhub.review_skin_clinic";
  UserPermission2["VIEW_PATIENTS_HUB"] = "colorhub.view_patients_hub";
  UserPermission2["USE_GLOBAL_SEARCH"] = "colorhub.use_global_search";
  UserPermission2["VIEW_PATIENT_RECORD"] = "colorhub.view_patient_record";
  UserPermission2["EDIT_PATIENT_DEMOGRAPHICS"] = "colorhub.edit_patient_demographics";
  UserPermission2["EDIT_PATIENT_CONTACT_INFO"] = "colorhub.edit_patient_contact_info";
  UserPermission2["EDIT_PATIENT_INSURANCE"] = "colorhub.edit_patient_insurance";
  UserPermission2["ADD_PATIENT_NOTES"] = "colorhub.add_patient_notes";
  UserPermission2["EDIT_PATIENT_NOTES"] = "colorhub.edit_patient_notes";
  UserPermission2["ARCHIVE_PATIENT_NOTES"] = "colorhub.archive_patient_notes";
  UserPermission2["VIEW_PATIENT_TIMELINE"] = "colorhub.view_patient_timeline";
  UserPermission2["VIEW_PATIENT_GOALS"] = "colorhub.view_patient_goals";
  UserPermission2["EDIT_PATIENT_GOALS"] = "colorhub.edit_patient_goals";
  UserPermission2["ADD_TASK"] = "colorhub.add_task";
  UserPermission2["EDIT_TASK"] = "colorhub.edit_task";
  UserPermission2["ADD_TASK_COMMENTS"] = "colorhub.add_task_comments";
  UserPermission2["EDIT_TASK_COMMENTS"] = "colorhub.edit_task_comments";
  UserPermission2["ARCHIVE_TASK_COMMENTS"] = "colorhub.archive_task_comments";
  UserPermission2["ADD_RISK_ASSESSMENT"] = "colorhub.add_risk_assessment";
  UserPermission2["ADD_DOCUMENT"] = "colorhub.add_document";
  UserPermission2["DELETE_DOCUMENT"] = "colorhub.delete_document";
  UserPermission2["DOWNLOAD_DOCUMENT"] = "colorhub.download_document";
  UserPermission2["EDIT_DOCUMENT"] = "colorhub.edit_document";
  UserPermission2["FAX_DOCUMENT"] = "colorhub.fax_document";
  UserPermission2["SHARE_DOCUMENT"] = "colorhub.share_document";
  UserPermission2["VIEW_DOCUMENT"] = "colorhub.view_document";
  UserPermission2["COLORHUB_ADMIN"] = "colorhub.admin_hub";
  UserPermission2["VIEW_CONTRACT"] = "colorhub.view_contract";
  UserPermission2["EDIT_CONTRACT"] = "colorhub.edit_contract";
  UserPermission2["FULFILL_FULFILLMENT_ORDERS"] = "colorhub.fulfill_fulfillment_orders";
  UserPermission2["VIEW_FULFILLMENT_ORDERS"] = "colorhub.view_fulfillment_orders";
  UserPermission2["VIEW_INBOUND_DOCUMENT"] = "colorhub.view_inbound_document";
  UserPermission2["EDIT_INBOUND_DOCUMENT"] = "colorhub.edit_inbound_document";
  UserPermission2["VIEW_SMS_MESSAGES"] = "colorhub.view_sms_messages";
  UserPermission2["SEND_SMS_MESSAGES"] = "colorhub.send_sms_messages";
  return UserPermission2;
})(UserPermission || {});
var ScopedRoleType = /* @__PURE__ */ ((ScopedRoleType2) => {
  ScopedRoleType2["COVID_OVERVIEW_DASHBOARD_VIEWER"] = "view_covid_overview_dashboard";
  ScopedRoleType2["COVID_PATIENTS_DASHBOARD_VIEWER"] = "view_covid_patients_dashboard";
  ScopedRoleType2["VACCINE_OVERVIEW_DASHBOARD_VIEWER"] = "view_vx_overview_dashboard";
  ScopedRoleType2["VACCINE_PATIENTS_DASHBOARD_VIEWER"] = "view_vx_patients_dashboard";
  ScopedRoleType2["VACCINE_AUDIT_DASHBOARD_VIEWER"] = "view_vx_audit_dashboard";
  ScopedRoleType2["CONTACT_MANAGER"] = "contact_manager";
  ScopedRoleType2["USER_MANAGER"] = "manage_users";
  ScopedRoleType2["API_KEY_MANAGER"] = "api_key_manager";
  ScopedRoleType2["LAB_OVERVIEW_DASHBOARD_VIEWER"] = "lab_overview_dashboard_viewer";
  ScopedRoleType2["LAB_PHI_DASHBOARD_VIEWER"] = "lab_phi_dashboard_viewer";
  ScopedRoleType2["ELIGIBILITY_DATA_MANAGER"] = "eligibility_data_manager";
  ScopedRoleType2["RESEARCH_OVERVIEW_DASHBOARD_VIEWER"] = "research_overview_dashboard_viewer";
  ScopedRoleType2["RESEARCH_CRO_OVERVIEW_DASHBOARD_VIEWER"] = "research_cro_overview_dashboard_viewer";
  ScopedRoleType2["RESEARCH_PATIENTS_DASHBOARD_VIEWER"] = "research_patients_dashboard_viewer";
  ScopedRoleType2["ESSENTIAL_CARE_OVERVIEW_DASHBOARD_VIEWER"] = "essential_care_overview_dashboard_viewer";
  ScopedRoleType2["ESSENTIAL_CARE_PATIENTS_DASHBOARD_VIEWER"] = "essential_care_patients_dashboard_viewer";
  ScopedRoleType2["CONSULTATIONS_OVERVIEW_DASHBOARD_VIEWER"] = "consultations_overview_dashboard_viewer";
  ScopedRoleType2["CONSULTATIONS_PATIENTS_DASHBOARD_VIEWER"] = "consultations_patients_dashboard_viewer";
  ScopedRoleType2["STI_OVERVIEW_DASHBOARD_VIEWER"] = "sti_overview_dashboard_viewer";
  ScopedRoleType2["STI_PATIENTS_DASHBOARD_VIEWER"] = "sti_patients_dashboard_viewer";
  ScopedRoleType2["ONSITE_BATCH_SHIPMENT_ADMIN"] = "onsite_batch_shipment_admin";
  ScopedRoleType2["VACCINATION_PLATFORM_SITE_MANAGER"] = "vaccination_platform_site_manager";
  ScopedRoleType2["VACCINATION_PLATFORM_SITE_STAFF"] = "vaccination_platform_site_staff";
  ScopedRoleType2["VACCINATION_PLATFORM_SCRIBE"] = "vaccination_platform_scribe";
  ScopedRoleType2["VACCINATION_PLATFORM_VACCINATING_CLINICIAN"] = "vaccination_platform_vaccinating_clinician";
  ScopedRoleType2["VACCINATION_PLATFORM_SITE_SCHEDULER"] = "vaccination_platform_site_scheduler";
  ScopedRoleType2["VACCINATION_PLATFORM_SITE_INVENTORY_MANAGER"] = "vaccination_platform_site_inventory_manager";
  ScopedRoleType2["VACCINATION_PLATFORM_BULK_INVENTORY_MANAGER"] = "vaccination_platform_bulk_inventory_manager";
  ScopedRoleType2["VACCINATION_PLATFORM_STAFF_MANAGER"] = "vaccination_platform_staff_manager";
  ScopedRoleType2["VACCINATION_PLATFORM_SUPER_ADMIN"] = "vaccination_platform_super_admin";
  ScopedRoleType2["PROMISE_OVERVIEW_DASHBOARD_VIEWER"] = "promise_overview_dashboard_viewer";
  ScopedRoleType2["PROMISE_PARTICIPANTS_DASHBOARD_VIEWER"] = "promise_participants_dashboard_viewer";
  return ScopedRoleType2;
})(ScopedRoleType || {});
var PermissionType = /* @__PURE__ */ ((PermissionType2) => {
  PermissionType2["UNKNOWN"] = "UNKNOWN";
  PermissionType2["ALLOWED"] = "ALLOWED";
  PermissionType2["NOT_ALLOWED"] = "NOT_ALLOWED";
  return PermissionType2;
})(PermissionType || {});
const EVENT_NEW_TAB = "newTabOpened";
const CHANNEL_NAME = "color-multi-tab-detector";
const globalMultiTabState = { hasMultipleTabs: false };
const updateGlobalMultiTabState = (hasMultipleTabs) => {
  globalMultiTabState.hasMultipleTabs = hasMultipleTabs;
};
function useMultiTabDetector() {
  const [hasMultipleTabs, setHasMultipleTabs] = useState(false);
  const broadcastChannelRef = useRef(null);
  const initializeBroadcastChannel = useCallback(() => {
    try {
      if (typeof BroadcastChannel === "undefined") {
        return;
      }
      const channel = new BroadcastChannel(CHANNEL_NAME);
      broadcastChannelRef.current = channel;
      channel.onmessage = (event) => {
        if (event.data === EVENT_NEW_TAB) {
          setHasMultipleTabs(true);
          updateGlobalMultiTabState(true);
        }
      };
      channel.postMessage(EVENT_NEW_TAB);
    } catch {
    }
  }, []);
  const destroy = useCallback(() => {
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.close();
      broadcastChannelRef.current = null;
    }
  }, []);
  const reset = useCallback(() => {
    setHasMultipleTabs(false);
    updateGlobalMultiTabState(false);
  }, []);
  useEffect(() => {
    initializeBroadcastChannel();
    return destroy;
  }, [initializeBroadcastChannel, destroy]);
  return {
    hasMultipleTabs,
    reset
  };
}
const dest$1 = constate(useMultiTabDetector);
const MultiTabDetectorProvider = dest$1[0];
dest$1[1];
const hasMultipleTabsOpen = () => {
  return globalMultiTabState.hasMultipleTabs;
};
const trackAnalyticsEvent = (event, properties = {}) => {
  setTimeout(() => {
    const eventProperties = {
      ...properties,
      ...hasMultipleTabsOpen() && { multiple_tabs_open: true }
    };
    trackSegmentEvent(event, eventProperties);
  }, 0);
};
const identifyAnalyticsUser = (props) => {
  setTimeout(() => {
    identifySegmentUser(props);
  }, 0);
};
const resetAnalyticsUser = () => {
  setTimeout(() => {
    resetSegmentUser();
  }, 0);
};
const logAnalyticsPageView = (name, properties) => {
  setTimeout(() => {
    const eventProperties = {
      ...properties,
      ...hasMultipleTabsOpen() && { multiple_tabs_open: true }
    };
    logSegmentPageView(name, eventProperties);
  }, 0);
};
const HTTP_UNATHENTICATED_STATUSES = /* @__PURE__ */ new Set([401, 403]);
const AUTH_HEARTBEAT_INTERVAL_IN_MSEC = 6e4;
function useIdentifySegmentUser() {
  const lastUserIdRef = useRef("");
  const updateSegmentUser = useCallback(
    (user, properties = {}) => {
      const patientId = user?.patient_id;
      if (patientId && patientId !== lastUserIdRef.current) {
        identifyAnalyticsUser({ patientId, isColorEmployee: user.is_color_employee, properties });
        lastUserIdRef.current = patientId;
      }
    },
    []
  );
  return { updateSegmentUser };
}
function clearSessionState() {
  clearBrowserStorage(BrowserStorageType.SESSION_STORAGE);
}
function useAuthState(props = {}) {
  const {
    baseUrl = "",
    sessionRefetchIntervalMs = AUTH_HEARTBEAT_INTERVAL_IN_MSEC,
    sessionRefetchIntervalInBackground = false,
    authUserStaleTimeMs = 0
  } = props;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { updateSegmentUser } = useIdentifySegmentUser();
  const { updateLaunchDarkly } = useLaunchDarklyUpdater();
  const resetState = useCallback(() => {
    clearSessionState();
    setIsAuthenticated(false);
  }, []);
  const sessionResponse = useAuthSession(
    {
      retry: false,
      refetchInterval: sessionRefetchIntervalMs,
      refetchIntervalInBackground: sessionRefetchIntervalInBackground,
      onSuccess: () => {
        setIsAuthenticated(true);
      },
      onError: (error) => {
        if (HTTP_UNATHENTICATED_STATUSES.has(error?.status)) {
          setIsAuthenticated(false);
        }
      },
      onSettled: async () => {
        await updateLaunchDarkly();
      }
    },
    baseUrl
  );
  const authenticatedUserResponse = useAuthenticatedUser(
    isAuthenticated,
    {
      refetchOnWindowFocus: isAuthenticated,
      staleTime: authUserStaleTimeMs,
      onSuccess: (user) => {
        updateSegmentUser(user);
        Sentry.setUser(user ? { id: user.id.toString() } : null);
      }
    },
    baseUrl
  );
  const {
    data: currentUser,
    refetch: fetchUser,
    isFetched: userIsFetched,
    remove: clearAuthenticatedUser
  } = authenticatedUserResponse;
  const {
    isFetched: hasInitializedSession,
    refetch: updateSession,
    error: sessionError
  } = sessionResponse;
  const signIn = useCallback(
    async (email, password, nextUrl) => {
      try {
        const createSessionUrl = appendQueryParams(`${baseUrl}${SESSIONS_ENDPOINT}`, {
          next: nextUrl
        });
        const response = await ApiClient.post(createSessionUrl, {
          json: {
            email,
            password
          }
        });
        const isMfaRequired = response?.is_mfa_required ?? false;
        if (isMfaRequired) {
          setIsAuthenticated(false);
          return {
            isSuccessful: false,
            mfaConfig: { factors: response?.factors ?? [] }
          };
        }
        const emailNeedsConfirmation = response?.email_needs_confirmation ?? false;
        if (emailNeedsConfirmation) {
          setIsAuthenticated(false);
          return {
            isSuccessful: false,
            emailNeedsConfirmation: true
          };
        }
        trackAnalyticsEvent("Sign In - Succeeded", {
          is_mfa: "no"
        });
        setIsAuthenticated(true);
        await updateLaunchDarkly();
        return { isSuccessful: true };
      } catch (error) {
        trackAnalyticsEvent("Sign In - Failed", {
          error: error instanceof Error ? error.message : error?.toString?.() ?? "",
          status: error instanceof ApiError ? error.status : ""
        });
        setIsAuthenticated(false);
        throw error;
      }
    },
    [updateLaunchDarkly, baseUrl]
  );
  const signOut = useCallback(async () => {
    try {
      await ApiClient.delete(`${baseUrl}${SESSIONS_ENDPOINT}`);
    } catch (error) {
      if (error.status !== 401) {
        throw error;
      }
    }
    clearAuthenticatedUser();
    clearSessionState();
    resetAnalyticsUser();
    Sentry.setUser(null);
    setIsAuthenticated(false);
    try {
      await updateSession();
    } catch {
    }
  }, [clearAuthenticatedUser, updateSession, baseUrl]);
  const sessionErrorIsUnauthenticatedStatus = hasInitializedSession && sessionError != null && HTTP_UNATHENTICATED_STATUSES.has(sessionError.status);
  const resetStateAndUpdateSession = useCallback(async () => {
    resetState();
    const updateSessionResponse = await updateSession();
    let attemptCount = 1;
    let hasSucceeded = updateSessionResponse.isSuccess;
    if (!hasSucceeded) {
      const retriedUpdateSessionResponse = await updateSession();
      attemptCount += 1;
      hasSucceeded = retriedUpdateSessionResponse.isSuccess;
    }
    trackAnalyticsEvent("Session Refresh", {
      status: hasSucceeded ? "succeeded" : "failed",
      attempt_count: attemptCount
    });
  }, [resetState, updateSession]);
  return {
    currentUser,
    hasInitializedUser: userIsFetched || sessionErrorIsUnauthenticatedStatus,
    hasInitializedSession,
    isAuthenticated,
    resetStateAndUpdateSession,
    signIn,
    signOut,
    fetchUser
  };
}
const dest = constate(useAuthState);
const AuthProvider = dest[0];
const useAuthContext = dest[1];
export {
  ALL_USER_ROLES as A,
  CollectionSiteRole as C,
  GENETICS_USER_ROLES as G,
  MultiTabDetectorProvider as M,
  PermissionType as P,
  ScopedRoleType as S,
  UserPermission as U,
  UserRole as a,
  CollectionSiteRoleCategory as b,
  AuthProvider as c,
  identifyAnalyticsUser as i,
  logAnalyticsPageView as l,
  resetAnalyticsUser as r,
  trackAnalyticsEvent as t,
  useAuthContext as u
};
//# sourceMappingURL=AuthContext-CRonWrIY.js.map
